import React, { useState } from "react";
import { STAGE_LABELS } from "@/lib/api";
import { ChevronDown, ChevronRight } from "lucide-react";
import JobCard from "./JobCard";

/**
 * One stage column on the Kanban board: header, drop-zone styling, empty-state,
 * and the list of <JobCard> children.
 *
 * Two UX affordances added to prevent the "endless page scroll" the user hit:
 *   1. Column body is `max-h-[calc(100vh-260px)] overflow-y-auto` so each stage
 *      scrolls independently — the outer page never grows.
 *   2. Column header has a chevron-toggle that collapses the entire column to a
 *      single row (title + count). Useful when only two or three stages matter.
 */
export default function KanbanColumn({
  stage,
  stages,
  jobs,
  bg,
  priorityStyles,
  selectedIds,
  draggedJobId,
  dragOverStage,
  isOverdue,
  staleMap,
  labelForJobClient,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  onSelect,
  onToggleSelect,
  onAdvance,
  onDuplicate,
}) {
  const items = jobs || [];
  // Start expanded — user can collapse manually via the chevron. We used to
  // auto-collapse empty columns on mount but that latched to the initial empty
  // state during async data-load and left every column collapsed. Manual control
  // is unsurprising and matches how they want to work through the board.
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className={`brut-card p-3 shrink-0 w-[280px] sm:w-[320px] lg:w-auto snap-start transition-all ${
        collapsed ? "" : "min-h-[300px]"
      } ${dragOverStage === stage ? "ring-4 ring-yellow-400 ring-offset-1" : ""}`}
      data-testid={`column-${stage}`}
      onDragOver={(e) => onDragOver(e, stage)}
      onDragLeave={() => dragOverStage === stage && onDragLeave()}
      onDrop={(e) => onDrop(e, stage)}
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className={`label-mono px-2 py-1.5 mb-3 border border-zinc-900 ${bg} w-full flex items-center gap-1 hover:bg-opacity-80 cursor-pointer`}
        data-testid={`column-toggle-${stage}`}
        aria-expanded={!collapsed}
        title={collapsed ? `Expand ${STAGE_LABELS[stage]}` : `Collapse ${STAGE_LABELS[stage]}`}
      >
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        <span className="flex-1 text-left truncate">{STAGE_LABELS[stage]}</span>
        <span className="font-mono text-[11px] font-bold">{items.length}</span>
      </button>
      {!collapsed && (
        <div
          className="space-y-2 overflow-y-auto pr-1"
          style={{ maxHeight: "calc(100vh - 280px)" }}
          data-testid={`column-body-${stage}`}
        >
          {items.map((j) => {
            const staleInfo = staleMap?.[j.id];
            return (
              <JobCard
                key={j.id}
                job={j}
                stage={stage}
                boardStages={stages}
                priorityStyles={priorityStyles}
                isSelected={selectedIds.has(j.id)}
                isDragging={draggedJobId === j.id}
                isOverdue={isOverdue(j)}
                isStale={!!staleInfo}
                staleDaysInStage={staleInfo?.days_in_stage}
                clientLabel={labelForJobClient(j)}
                onSelect={onSelect}
                onToggleSelect={onToggleSelect}
                onAdvance={onAdvance}
                onDuplicate={onDuplicate}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
              />
            );
          })}
          {items.length === 0 && (
            <div className="text-center text-xs font-mono text-zinc-400 py-8 border-2 border-dashed border-zinc-300">
              — empty —
            </div>
          )}
        </div>
      )}
    </div>
  );
}
